import { useState } from "react";
import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import dayjs from "dayjs";
import { lngDetect } from "../../../../helpers/lngDetect";
import {
  csvExporter,
  handleFilterChange,
  xlsxExporter,
} from "../../../../helpers";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useOutletContext } from "react-router-dom";
import { useOrganization } from "../../../../context/OrganizationContext";
import * as XLSX from "xlsx";
import {
  exportReports,
  orderReportList,
} from "../../../../services/eventReportService";
import ExportDropdown from "../EventReport/ExportDropdown";
import Button from "../../../form/Button";
import DatePicker from "../../../form/DatePicker";
import Badge from "../../../elements/Badge";
import { countList, eventCountList } from "../../../../services/eventService";
import { organizationTeamItem } from "../../../../services/organizationService";
import { useTranslation } from "react-i18next";
import { classNames } from "../../../../helpers/classNames";

const productTypeList = [
  { name: "Ticket", value: "ticket", type: "checkbox" },
  { name: "Addon", value: "addons-event", type: "checkbox" },
];

const getTicketTypeStyle = (type) => {
  const typeLower = type?.toLowerCase();

  const typeStyles = {
    ticket: {
      backgroundColor: "#EDE9FE", // Purple background
      color: "#7C3AED", // Purple text
      borderColor: "#8B5CF6", // Purple border
    },
    "add-on": {
      backgroundColor: "#ECFDF5", // Emerald background
      color: "#059669", // Emerald text
      borderColor: "#10B981", // Emerald border
    },
  };

  return (
    typeStyles[typeLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

const TicketList = ({ selectedEvents = [], setDrawer = () => {} }) => {
  const { t } = useTranslation();
  const { event } = useOutletContext();
  const { selectedOrganization } = useOrganization();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  // Fetch orders data
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "sales-reports",
      filters,
      search,
      selectedEvents?.map((item) => item?._id),
    ],
    queryFn: async () => {
      const res = await organizationTeamItem({
        ...filters,
        ...(search && { keyword: search }),
        event: selectedEvents?.map((item) => item?._id),
        orderStatus: ["confirmed"],
        viewAs: "reporting",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return selectedEvents?.map((item) => item?._id)?.length > 0
        ? res?.docs
        : {};
    },
  });

  // Handle export functionality with client-side download and API call
  const handleExport = async (format) => {
    setIsExportOpen(false);
    toast.info("Preparing export data...");
    try {
      const exportResponse = await organizationTeamItem({
        ...filters,
        ...(search && { keyword: search }),
        event: selectedEvents?.map((item) => item?._id),
        orderStatus: ["confirmed"],
        viewAs: "reporting",
        limit: data?.metadata?.totalDocs, // Large number to get all records
        page: 1,
      });

      if (exportResponse?.docs?.status !== 200) {
        toast.error(exportResponse?.message || "Failed to fetch export data");
        return;
      }

      const orderData = exportResponse?.docs?.data || [];

      if (!orderData || orderData.length === 0) {
        toast.warning("No data available to export");
        return;
      }
      // Transform the order data for export
      const exportData = orderData.map((ticket) => ({
        "Ticket Name": lngDetect(ticket?.ticketInfo?.name) || "-",
        "Ticket Type":
          ticket?.ticketInfo?.type
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ") || "-",
        "Ticket Quantity": ticket?.ticketData?.quantity || 0,
        "Paid Tickets Sold": ticket?.totalQuantity || 0,
        "Gross Sales": "$" + ticket?.totalFinalPrice || 0,
      }));

      if (format === "csv") {
        csvExporter(exportData, "sales-by-ticket-type").then(async (result) => {
          if (result?.status === 200) {
            const resp = await exportReports({
              name: result?.fileName,
              file: result?.file,
              type: result?.fileCategory,
              status: "success",
              organization: selectedOrganization,
            });
            if (resp?.status !== 201) {
              toast.error(resp?.message || "Something wrong..");
            }
          }
        });
      } else if (format === "xlsx") {
        xlsxExporter(exportData, "sales-by-ticket-type").then(
          async (result) => {
            if (result?.status === 200) {
              const resp = await exportReports({
                name: result?.fileName,
                file: result?.file,
                type: result?.fileCategory,
                status: "success",
                organization: selectedOrganization,
              });
              if (resp?.status !== 201) {
                toast.error(resp?.message || "Something wrong..");
              }
            }
          }
        );
      }
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExportOpen(false);
    }
  };

  const tableHeadData = [
    {
      _id: "ticketName",
      name: t("Ticket Name"),
      align: "left",
    },
    {
      _id: "ticketType",
      name: t("Ticket Type"),
      align: "left",
      ...tableFilter({
        filterKey: "ticketType",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [...productTypeList],
      }),
    },
    // {
    //   _id: "ticketPrice",
    //   name: "Ticket Price",
    //   align: "left",
    // },
    {
      _id: "ticketQuantity",
      name: t("Ticket Quantity"),
      align: "left",
    },
    {
      _id: "paidTicketsSold",
      name: t("Paid Tickets Sold"),
      align: "left",
    },
    {
      _id: "grossSales",
      name: t("Gross Sales"),
      align: "left",
    },
  ];

  // Table Body Data
  const tableData = data?.data?.map((item) => {
    const ticketType = item?.ticketInfo?.type || "general-admission";
    const ticketTypeText = ticketType
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    const ticketTypeStyle = getTicketTypeStyle(ticketType);

    return {
      _id: item?._id,
      rowData: [
        {
          _id: "salesTicket",
          type: "text",
          align: "left",
          cellClass: "!min-w-[120px]",
          data: lngDetect(item?.ticketInfo?.name),
        },
        {
          _id: "salesChannel",
          type: "text",
          align: "left",
          cellClass: "!min-w-[120px]",
          data: (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
              style={{
                backgroundColor: ticketTypeStyle.backgroundColor,
                color: ticketTypeStyle.color,
                borderColor: ticketTypeStyle.borderColor,
              }}
            >
              {ticketTypeText}
            </span>
          ),
        },
        // {
        //   _id: "ticketPrice",
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px] font-medium",
        //   data: "$" + (item?.ticketInfo?.price || 0).toString(),
        // },
        {
          _id: "ticketCount",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] font-medium",
          data: item?.ticketData?.quantity || 0,
        },
        {
          _id: "paidTicketsSold",
          type: "text",
          align: "left",
          cellClass: "!min-w-[120px] text-center font-medium",
          data: item?.totalQuantity || 0,
        },
        {
          _id: "complimentaryTicketsSold",
          type: "text",
          align: "left",
          cellClass: "!min-w-[160px] text-center font-medium",
          data: `${item?.eventData?.currency?.symbol || "$"} ${(
            item?.totalFinalPrice || 0
          ).toFixed(2)}`,
        },
      ],
    };
  });

  const { data: eventCount, isLoading: eventCountLoading } = useQuery({
    queryKey: ["event-count-data", selectedEvents?.map((item) => item?._id)],
    queryFn: async () => {
      const res = await countList({
        event: selectedEvents?.map((item) => item?._id),
        orderStatus: ["confirmed"],
        sortQuery: "-createdAt",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
  });

  const statistics = [
    { label: t("Ticket Sold"), value: eventCount?.ticketsSold || 0 },
    { label: t("Orders"), value: eventCount?.totalOrder || 0 },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-5 absolute -top-28 right-6">
        {/* <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-gray-600 text-sm font-medium mb-2">
                {t("Net Sales")}
              </h3>
              <div className="text-3xl font-bold text-gray-900">
                $ {eventCount?.netSales || 0}
              </div>
            </div> */}

        {statistics?.map((s, i) => (
          <div
            key={i?.toString()}
            className={classNames(
              "relative pl-4",
              "before:content-[''] before:w-[6px] before:h-full before:bg-green-300 before:rounded-full before:absolute before:top-0 before:left-0"
            )}
          >
            <div className="text-sm text-gray-400 font-medium">{s?.label}</div>
            <div className="text-lg font-semibold text-deep-purple">
              {s?.value}
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-72">
              <Search
                placeholder={t("Search here")}
                divClasses={"mb-0"}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
          </div>
          <div className="flex gap-4 items-center">
            <Button
              buttonIcon={"fa-light fa-calendar-days"}
              buttonLabel={`${t("Select Events")} (${
                selectedEvents?.length || 0
              })`}
              buttonFunction={() => {
                setDrawer({ isOpen: true, data: null });
              }}
            />
            <ExportDropdown
              onExport={handleExport}
              isOpen={isExportOpen}
              onToggle={() => setIsExportOpen(!isExportOpen)}
            />
          </div>
        </div>

        {/* Orders Table */}
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          originalData={[...(data?.data || [])]}
          setOriginalData={(val) => {
            queryClient.setQueryData(
              ["order", filters, search, selectedOrganization],
              (state) => {
                if (state) {
                  return { ...state, data: val };
                }
              }
            );
          }}
          // handleOrdering={(newData, oldData) =>
          //   handleOrdering({ newData, oldData })
          // }
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
        />
      </div>
    </div>
  );
};

export default TicketList;
